<template>
    <div
        v-if="parameterType"
        class="widget-other-filters"
    >
        <RedesignFormLabel
            :label="templateParameterName"
            :info-text="templateParameterDescription"
            class="mb-2"
        />
        <div class="w-full">
            <component
                :is="currentComponent.component"
                v-model="componentValue"
                :placeholder="t('settings.add.filter')"
                v-bind="currentComponent.parameterData"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import { computed, type Component } from 'vue'
import { VcInput, VcInputNumber, VcSelect, VcRadioButtonsGroup } from '@voicenter-team/voicenter-ui-plus'
import { WidgetConfig, WidgetParameterValueJson } from '@/types/dashboard'
import cloneDeep from 'lodash/cloneDeep'
import {
    getTemplateParameterHelpDescription,
    getTemplateParameterHelpName
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { parseJsonStringOrReturnString } from '@/util/objectUtil'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface ComponentEntry {
    component: Component | ''
    parameterData: Record<string, any>
}

const components: Record<number, ComponentEntry> = {
    1: {
        component: VcInput,
        parameterData: {
            type: 'text'
        }
    },
    2: {
        component: VcInputNumber,
        parameterData: {
            minValue: 0,
            step: 1
        }
    },
    3: {
        component: '',
        parameterData: {}
    },
    4: {
        component: VcInputNumber,
        parameterData: {
            step: 1
        }
    },
    5: {
        component: VcSelect,
        parameterData: {
            multiple: false,
            filterable: true
        }
    },
    6: {
        component: VcSelect,
        parameterData: {
            multiple: true,
            filterable: true,
            clearable: true
        }
    },
    7: {
        component: VcRadioButtonsGroup,
        parameterData: {
            config: {
                valueKey: 'value',
                labelKey: 'label'
            }
        }
    },
    16: {
        component: '',
        parameterData: {}
    }
}

const props = defineProps<{
    value: WidgetConfig
}>()

const emit = defineEmits([ 'input', 'update:value' ])

/* Computed */
const model = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('input', val)
        emit('update:value', val)
    }
})
const parameterType = computed(() => {
    return model.value.ParameterType
})
const templateParameterName = computed(() => {
    return getTemplateParameterHelpName(model.value)
})
const templateParameterDescription = computed(() => {
    return getTemplateParameterHelpDescription(model.value)
})
const componentValue = computed({
    get () {
        if (model.value.WidgetParameterJson === 1) {
            return model.value.WidgetParameterValueJson
        } else {
            return parseJsonStringOrReturnString(model.value.WidgetParameterValue || '')
        }
    },
    set (val) {
        const updatedModel: Partial<WidgetConfig> = {}

        if (model.value.WidgetParameterJson === 1) {
            updatedModel.WidgetParameterValueJson = cloneDeep(val) as WidgetParameterValueJson
        } else {
            updatedModel.WidgetParameterValue = typeof (val) === 'string' ? val : JSON.stringify(val)
        }

        model.value = {
            ...model.value,
            ...updatedModel
        }
    }
})

const radioOptions = computed(() => [
    {
        label: t('general.enabled'),
        value: '1'
    },
    {
        label: t('general.disabled'),
        value: '0'
    }
])

const currentComponent = computed(() => {
    if (!parameterType.value || !components[parameterType.value]) {
        return {
            component: '',
            parameterData: {}
        }
    }

    const entry = components[parameterType.value]
    const filterParameterData = model.value.ParameterData || {}
    const parameterData = { ...entry.parameterData }

    // Select types: build options and config from ParameterData
    if (parameterType.value === 5 || parameterType.value === 6) {
        parameterData.options = filterParameterData.data || []
        parameterData.config = {
            labelKey: 'Key',
            valueKey: 'Value'
        }
        parameterData.class = 'w-full'
    }

    // Radio type: provide translated radios
    if (parameterType.value === 7) {
        parameterData.radios = radioOptions.value
    }

    return {
        ...entry,
        parameterData
    }
})
</script>
